import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAnalysisBacktest, getBacktestRunStatus, getStoredBacktestsByIds, listBacktestCombos, listStoredBacktests, runFullBacktest } from 'api/backtestApi';
import { ApiError } from 'api/restClient';
import type { AnalysisHistoryFilters, BacktestAggregate, BacktestCombo, BacktestRollup, BacktestRunScope, BacktestRunState, BacktestSettings } from 'types/history';

type Status = 'idle' | 'loading' | 'success' | 'error';

/**
 * The rule set a view is reading. Results are stored per rule set, so this —
 * not a lone window number — is what identifies a sweep.
 */
export interface BacktestRuleSet {
	window: number;
	/** Time stop in candles; 0 = disabled. */
	closeWindow: number;
	/** Stop-loss distance multiplier; 1 = the stop each analysis proposed. */
	stopMult: number;
	/**
	 * Which replay engine produced the results. Part of the rule set for the same
	 * reason the multiplier is: a fixed engine gives the same analysis a different
	 * verdict, so its sweep is a different answer and must not collapse into the
	 * one it should be compared against. 1 is the pre-fix engine, whose forward
	 * window opened one candle late.
	 */
	replayVersion: number;
}

/** Matches the backend's own fallback, used only until settings have loaded. */
export const DEFAULT_RULES: BacktestRuleSet = { window: 6, closeWindow: 0, stopMult: 1, replayVersion: 1 };

/** A rule set as a server payload spells it — snake_case, and any knob may be
 *  missing entirely when the backend predates it. */
export interface RuleSetPayload {
	window?: number | null;
	close_window?: number | null;
	stop_mult?: number | null;
	replay_version?: number | null;
}

/**
 * A server payload as a rule set, with every absent knob falling back to the
 * baseline.
 *
 * The fallback is the point, not a formality. Frontend and backend ship
 * separately — and a blue-green flip guarantees a window where a loaded page is
 * talking to the other color — so a payload written before a knob existed is a
 * normal thing to receive, not a broken one. Copying those fields raw put
 * `undefined` into the rule set, which rendered as "SL undefined×" and, because
 * `undefined !== currentEngine`, tripped the stale-engine guard and removed the
 * run buttons: an older backend made the sweep controls unreachable.
 *
 * Every construction site goes through here so that can only be fixed once.
 */
export const toRuleSet = (r: RuleSetPayload | null | undefined): BacktestRuleSet => ({
	window: r?.window ?? DEFAULT_RULES.window,
	closeWindow: r?.close_window ?? DEFAULT_RULES.closeWindow,
	stopMult: r?.stop_mult ?? DEFAULT_RULES.stopMult,
	replayVersion: r?.replay_version ?? DEFAULT_RULES.replayVersion,
});

export const sameRules = (a: BacktestRuleSet, b: BacktestRuleSet): boolean => a.window === b.window && a.closeWindow === b.closeWindow && a.stopMult === b.stopMult && a.replayVersion === b.replayVersion;

export const ruleKey = (p: BacktestRuleSet): string => `${p.window}:${p.closeWindow}:${p.stopMult}:v${p.replayVersion}`;

/**
 * The active rule set, seeded from the saved settings.
 *
 * Lives above both data hooks because each needs it: the backtest surface reads
 * results for the pair, and the history query filters verdicts by it. Keeping it
 * inside useBacktestList would make the two circular, since that hook takes the
 * rows the history query returns.
 */
export function useRuleSet(settings: BacktestSettings | null): [BacktestRuleSet, (p: BacktestRuleSet) => void] {
	const [pair, setPair] = useState<BacktestRuleSet>(DEFAULT_RULES);
	// Settings arrive after the first render, so the seed is an effect rather than
	// lazy initial state. Applied once: after that the pair belongs to the user,
	// and a later settings save must not yank the view off the sweep they are
	// reading. Without this the view always opened on 6 / no-stop — which, once
	// the summary bar follows the filters, would read as "the stats are broken"
	// when it is really "you are looking at a pair nobody swept".
	const seeded = useRef(false);
	useEffect(() => {
		if (seeded.current || !settings) return;
		seeded.current = true;
		// `entry_window` is the settings row's name for the same knob the stored
		// rows call `window`, so it is mapped here rather than in toRuleSet.
		setPair(toRuleSet({ ...settings, window: settings.entry_window }));
	}, [settings]);

	return [pair, setPair];
}

const POLL_MS = 2000;

function errMessage(e: unknown, adminHint = false): string {
	if (e instanceof ApiError) {
		if (e.status === 403 && adminHint) {
			return 'Your account is not permitted to run backtests (admin only).';
		}
		return `${e.message} (${e.status})`;
	}
	return e instanceof Error ? e.message : 'Failed to load backtest';
}

export interface UseBacktestListResult {
	aggregate: BacktestAggregate | null;
	byId: Map<string, BacktestRollup>;
	/** Rule sets that actually have stored results, for the preset picker. */
	combos: BacktestCombo[];
	status: Status;
	loadingIds: boolean;
	error: string | null;
	refresh: () => void;
	total: number;
	lastComputedAt: string | null;
	run: BacktestRunState | null;
	startRun: (scope: BacktestRunScope) => void;
	/** Analyses with no settled verdict under this rule set — the size of what a
	 *  "missing" run would process. Unaffected by the filters. */
	missingCount: number;
}

/**
 * Drives the backtest surface for History: the whole-database aggregate summary
 * bar, the background sweep (with progress polling), and — keyed to the exact
 * `ids` currently visible on the page — the per-row verdicts. Fetching verdicts
 * by the visible ids (rather than a separately-paginated stored page) keeps the
 * column aligned even though mock / no-trade rows shift the two lists.
 *
 * Everything is scoped to a *rule set*, because sweeps run under different rules
 * coexist in the database. `settings` seeds the active one; the user can then
 * switch to any other rule set that has stored results without re-running.
 *
 * The aggregate is scoped to `filters` as well, so the summary bar describes the
 * set the table is showing. `missingCount` deliberately is not — it sizes a
 * sweep, and sweeps cover all history.
 */
export function useBacktestList(filters: AnalysisHistoryFilters, ids: string[], pair: BacktestRuleSet): UseBacktestListResult {
	const [aggregate, setAggregate] = useState<BacktestAggregate | null>(null);
	const [items, setItems] = useState<BacktestRollup[]>([]);
	const [total, setTotal] = useState(0);
	const [lastComputedAt, setLastComputedAt] = useState<string | null>(null);
	const [combos, setCombos] = useState<BacktestCombo[]>([]);
	const [missingCount, setMissingCount] = useState(0);
	const [status, setStatus] = useState<Status>('idle');
	const [loadingIds, setLoadingIds] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [run, setRun] = useState<BacktestRunState | null>(null);

	// Stable identity for the id list so effects don't re-run every render.
	const idsKey = ids.join(',');
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const stableIds = useMemo(() => ids, [idsKey]);

	const aggAbortRef = useRef<AbortController | null>(null);
	const idsAbortRef = useRef<AbortController | null>(null);
	const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const mountedRef = useRef(true);
	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
			aggAbortRef.current?.abort();
			idsAbortRef.current?.abort();
			if (pollRef.current) clearInterval(pollRef.current);
		};
	}, []);

	// Stable identity for the filter object so the effect below doesn't re-run on
	// every render just because HistoryView rebuilt the literal.
	const filtersKey = JSON.stringify(filters);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const stableFilters = useMemo(() => filters, [filtersKey]);

	// Filtered aggregate + meta (independent of the visible page).
	const loadAggregate = useCallback(() => {
		aggAbortRef.current?.abort();
		const ctrl = new AbortController();
		aggAbortRef.current = ctrl;
		setStatus('loading');
		setError(null);
		// limit: 1 — only the aggregate and the counts are used here; per-row
		// verdicts come from the by-ids call below, which is aligned to the page.
		listStoredBacktests({ ...stableFilters, window: pair.window, closeWindow: pair.closeWindow, stopMult: pair.stopMult, replayVersion: pair.replayVersion, limit: 1, offset: 0 }, ctrl.signal)
			.then((res) => {
				if (!mountedRef.current || ctrl.signal.aborted) return;
				setAggregate(res.aggregate);
				setTotal(res.total);
				setLastComputedAt(res.last_computed_at);
				setMissingCount(res.missing_count ?? 0);
				setStatus('success');
			})
			.catch((e: unknown) => {
				if (!mountedRef.current || ctrl.signal.aborted) return;
				setError(errMessage(e, true));
				setStatus('error');
			});

		// Which pairs are available to switch to. Cheap (one GROUP BY) and it has to
		// follow every sweep, so it rides along with the aggregate.
		listBacktestCombos(ctrl.signal)
			.then((c) => {
				if (!mountedRef.current || ctrl.signal.aborted) return;
				setCombos(c);
			})
			.catch(() => undefined); // the picker degrades to the active rule set only
	}, [stableFilters, pair]);

	// Verdicts for exactly the visible rows.
	const loadByIds = useCallback(() => {
		idsAbortRef.current?.abort();
		if (!stableIds.length) {
			setItems([]);
			return;
		}
		const ctrl = new AbortController();
		idsAbortRef.current = ctrl;
		setLoadingIds(true);
		getStoredBacktestsByIds(pair.window, pair.closeWindow, pair.stopMult, pair.replayVersion, stableIds, ctrl.signal)
			.then((rows) => {
				if (!mountedRef.current || ctrl.signal.aborted) return;
				setItems(rows);
				setLoadingIds(false);
			})
			.catch(() => {
				if (!mountedRef.current || ctrl.signal.aborted) return;
				setLoadingIds(false);
			});
	}, [pair, stableIds]);

	useEffect(() => {
		loadAggregate();
	}, [loadAggregate]);
	useEffect(() => {
		loadByIds();
	}, [loadByIds]);

	const refresh = useCallback(() => {
		loadAggregate();
		loadByIds();
	}, [loadAggregate, loadByIds]);

	const stopPolling = useCallback(() => {
		if (pollRef.current) {
			clearInterval(pollRef.current);
			pollRef.current = null;
		}
	}, []);

	const beginPolling = useCallback(() => {
		stopPolling();
		pollRef.current = setInterval(() => {
			getBacktestRunStatus()
				.then((s) => {
					if (!mountedRef.current) return;
					setRun(s);
					if (s.status !== 'running') {
						stopPolling();
						refresh(); // sweep finished — refresh aggregate + visible verdicts
					}
				})
				.catch(() => stopPolling());
		}, POLL_MS);
	}, [refresh, stopPolling]);

	// On mount, surface an already-running sweep (e.g. started in another tab).
	useEffect(() => {
		getBacktestRunStatus()
			.then((s) => {
				if (!mountedRef.current) return;
				setRun(s);
				if (s.status === 'running') beginPolling();
			})
			.catch(() => undefined);
	}, [beginPolling]);

	const startRun = useCallback(
		(scope: BacktestRunScope) => {
			runFullBacktest(pair.window, pair.closeWindow, pair.stopMult, scope)
				.then((s) => {
					if (!mountedRef.current) return;
					setRun(s);
					beginPolling();
				})
				.catch((e: unknown) => setError(errMessage(e, true)));
		},
		[pair, beginPolling],
	);

	const byId = useMemo(() => {
		const m = new Map<string, BacktestRollup>();
		for (const r of items) m.set(r.audit_id, r);
		return m;
	}, [items]);

	return {
		aggregate,
		byId,
		combos,
		status,
		loadingIds,
		error,
		refresh,
		total,
		lastComputedAt,
		run,
		startRun,
		missingCount,
	};
}

export interface UseAnalysisBacktestResult {
	rollup: BacktestRollup | null;
	status: Status;
	error: string | null;
}

/** Backtests a single analysis (with candles) for the detail modal's chart. */
export function useAnalysisBacktest(id: string, pair: BacktestRuleSet): UseAnalysisBacktestResult {
	const [rollup, setRollup] = useState<BacktestRollup | null>(null);
	const [status, setStatus] = useState<Status>('idle');
	const [error, setError] = useState<string | null>(null);

	// Depend on the primitive fields, not the object: a fresh {…} literal from the
	// parent's render would otherwise re-fetch on every keystroke elsewhere.
	const { window, closeWindow, stopMult } = pair;

	useEffect(() => {
		const ctrl = new AbortController();
		setStatus('loading');
		setError(null);
		setRollup(null);
		getAnalysisBacktest(id, { window, closeWindow, stopMult }, ctrl.signal)
			.then((r) => {
				if (ctrl.signal.aborted) return;
				setRollup(r);
				setStatus('success');
			})
			.catch((e: unknown) => {
				if (ctrl.signal.aborted) return;
				setError(errMessage(e));
				setStatus('error');
			});
		return () => ctrl.abort();
	}, [id, window, closeWindow, stopMult]);

	return { rollup, status, error };
}
